// React
import React from 'react';
import { Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
// Locales
import translate from 'locales/i18n';
// Material components
import { withStyles } from '@mui/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import SearchIcon from '@mui/icons-material/Search';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { FormControlLabel, Switch } from '@mui/material';
import DialogConfirm from '../../dialogs/commons/DialogConfirm';
import Pagination from '@mui/lab/Pagination';
// PCP Components
import DialogManageAccount from 'components/dialogs/account/DialogManageAccount/DialogManageAccount';
import DialogExportAccount from 'components/dialogs/account/DialogExportAccount';
import DialogDuplicateAccount from 'components/dialogs/account/DialogDuplicateAccount';
import AccountRow from 'components/rows/AccountRow';
// Stores
import LoggedUserStore from 'stores/LoggedUserStore';
import AccountStore from 'stores/AccountStore';
// Action
import AccountAction from 'actions/AccountAction';
import SiteStore from 'stores/SiteStore';
// Style
import styles from 'components/page/AccountManagerPage/AccountManagerPage.style';

class AccountsManagerPage extends React.PureComponent {

    state = {
        accounts: AccountStore.getAccounts(),
        search: '',
        isLoading: AccountStore.isLoading(),
        sort: 'alpha_asc',
        module: '',
        type: '',
        isActive: false,
        dialogManageAccountOpen: false,
        dialogExportAccountOpen: false,
        dialogDuplicateAccountOpen: false,
        currentAccount: null,
        sites: SiteStore.getSites(),
        dialogConfirmActive: { open: false, account: null },
        page: 1,
        count: AccountStore.getCount()
    };

    static propTypes = {
        classes: PropTypes.object.isRequired
    };

    componentDidMount() {
        AccountStore.addChangeListener(this.onAccountStoreChange);
        SiteStore.addChangeListener(this.onSiteStoreChange);

        if (AccountStore.getAccounts().length === 0 && LoggedUserStore.isLogged())
            this.getAccounts();

        this.mounted = true;
    }

    componentWillUnmount() {
        AccountStore.removeChangeListener(this.onAccountStoreChange);
        SiteStore.removeChangeListener(this.onSiteStoreChange);

        this.mounted = false;
    }

    onAccountStoreChange = () => {
        if (!this.mounted) {
            return false;
        }

        this.setState({
            accounts: AccountStore.getAccounts(),
            isLoading: AccountStore.isLoading(),
            count: AccountStore.getCount()
        });
    };

    onSiteStoreChange = () => {
        if (!this.mounted) {
            return false;
        }

        this.setState({
            sites: SiteStore.getSites(),
        });
    };

    getAccounts = () => {
        const { sort, isActive, module, type, search, page } = this.state;

        AccountAction.getAccounts({
            sort,
            isActive,
            type,
            module,
            page,
            search
        });
    };

    deleteAccount = id => {
        AccountAction.deleteAccount(id);
    };

    exportAccount = (options, accountId) => {
        if (options.exportType === 'additionalData') {
            AccountAction.exportMissingAdditionalData(options, accountId);
        } else if (options.exportType === 'accountInfos') {
            AccountAction.exportData(options, accountId);
        }
    };

    close = () => {
        this.setState({
            dialogManageAccountOpen: false,
            dialogExportAccountOpen: false,
            currentAccount: null
        });
    };

    renderDialogManageAccount = () =>
        <DialogManageAccount close={this.close}
            open={this.state.dialogManageAccountOpen}
            selectedAccount={this.state.currentAccount}
            key={this.state.currentAccount ? this.state.currentAccount.id + ' dialog manage account' : 'dialog manage account'}
            sites={this.state.sites}
        />;

    renderDialogExportAccount = () =>
        <DialogExportAccount
            open={this.state.dialogExportAccountOpen}
            close={this.close}
            exportData={(options, accountId) => this.exportAccount(options, accountId)}
            account={this.state.currentAccount}
            isLoadingExport={this.state.isLoadingExport}
            exportError={this.state.exportError}
            key={this.state.currentAccount ? this.state.currentAccount.id + ' dialog export account' : 'dialog export account'} />;

    renderDialogDuplicateAccount = () =>
        <DialogDuplicateAccount
            open={this.state.dialogDuplicateAccountOpen}
            close={() => this.setState({ dialogDuplicateAccountOpen: false })}
            account={this.state.accountToDuplicate}
            key={this.state.accountToDuplicate ? this.state.accountToDuplicate.id + ' dialog duplicate account' : 'dialog duplicate account'} />;

    renderDialogConfirm = () =>
        <DialogConfirm
            open={this.state.dialogConfirmActive.open}
            close={() => this.setState({ dialogConfirmActive: { open: false, account: null } })}
            title={this.state.dialogConfirmActive.account?.isActive
                ? translate.i18n('WARNING_DEACTIVATE_ACCOUNT', { name: this.state.dialogConfirmActive.account?.name })
                : translate.i18n('WARNING_ACTIVATE_ACCOUNT', { name: this.state.dialogConfirmActive.account?.name })}
            text={this.state.dialogConfirmActive.account?.isActive ?
                [translate.i18n('WARNING_DEACTIVATE_1'), translate.i18n('WARNING_DEACTIVATE_2'), translate.i18n('WARNING_DEACTIVATE_3')]
                : [translate.i18n('WARNING_ACTIVATE_1'), translate.i18n('WARNING_ACTIVATE_2'), translate.i18n('WARNING_ACTIVATE_3')]}
            callback={() => AccountAction.updateAccount({ isActive: !this.state.dialogConfirmActive.account.isActive }, this.state.dialogConfirmActive.account.id)}
        />;

    render() {
        const { classes } = this.props;

        if (!LoggedUserStore.isLogged()) {
            return (
                <Redirect to='/login' />
            );
        }

        return (
            <div className={classes.root}>
                <div className={classes.header}>
                    <Button
                        variant='contained'
                        color='primary'
                        className={classes.headerHeight}
                        onClick={() => this.setState({ dialogManageAccountOpen: true })}>
                        <AddIcon/>
                        Account
                    </Button>
                    <TextField
                        id='searchfield'
                        className={classes.searchBar}
                        size='small'
                        variant='outlined'
                        value={this.state.search}
                        onChange={event => this.setState({ search: event.target.value })}
                        placeholder={'Account\'s name'}
                        margin={'dense'}
                        onKeyPress={event => event.key === 'Enter' && this.getAccounts()}
                        InputProps={{
                            startAdornment: <InputAdornment position='start'><SearchIcon className={classes.searchIcon} /></InputAdornment>
                        }}
                    />
                    <FormControl variant='outlined'
                        disabled={this.state.isSearchLoading}
                        size='small'
                        classes={{
                            root: classes.formControlMode
                        }}>
                        <InputLabel
                            htmlFor='outlined'
                            margin={'dense'}>
                            Sort
                        </InputLabel>
                        <Select
                            value={this.state.sort}
                            onChange={event => this.setState({ sort: event.target.value }, () => this.getAccounts())}
                            label='Sort'
                        >
                            <MenuItem value={'alpha_asc'}>Alphabétique</MenuItem>
                            <MenuItem value={'alpha_desc'}>Anti alphabétique</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl variant='outlined'
                        disabled={this.state.isSearchLoading}
                        size='small'
                        classes={{
                            root: classes.formControlMode
                        }}>
                        <InputLabel
                            htmlFor='outlined'
                            margin={'dense'}>
                            Type
                        </InputLabel>
                        <Select
                            value={this.state.type}
                            onChange={event => this.setState({ type: event.target.value }, () => this.getAccounts())}
                            label='Type'
                        >
                            <MenuItem value={''}>All</MenuItem>
                            <MenuItem value={'RETAILER'}>Retailer</MenuItem>
                            <MenuItem value={'SUPPLIER'}>Supplier</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl variant='outlined'
                        disabled={this.state.isSearchLoading}
                        size='small'
                        classes={{
                            root: classes.formControlMode
                        }}>
                        <InputLabel
                            htmlFor='outlined'
                            margin={'dense'}>
                            Module
                        </InputLabel>
                        <Select
                            value={this.state.module}
                            onChange={event => this.setState({ module: event.target.value }, () => this.getAccounts())}
                            label='Module'
                        >
                            <MenuItem value={''}>All</MenuItem>
                            <MenuItem value={'SPE'}>Price</MenuItem>
                            <MenuItem value={'ADBOX'}>Adbox</MenuItem>
                            <MenuItem value={'PERF'}>Performances</MenuItem>
                            <MenuItem value={'SLIDER'}>Slider</MenuItem>
                            <MenuItem value={'PRICING_ENGINE'}>Pricing Engine</MenuItem>
                            <MenuItem value={'TRENDS'}>Trends</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl
                        disabled={this.state.isSearchLoading}
                        classes={{
                            root: classes.isActiveSwitch
                        }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={this.state.isActive}
                                    onChange={() => this.setState({ isActive: !this.state.isActive }, () => this.getAccounts())}
                                    name='isActive'
                                    color='primary'
                                />
                            }
                            label='Active accounts'
                        />
                    </FormControl>
                </div>
                <Paper className={classes.paper}>
                    {this.state.isLoading ?
                        <div className={classes.loaderContainer}><CircularProgress /></div>
                        :
                        <Table className={classes.table} stickyHeader size='small'>
                            <TableHead>
                                <TableRow>
                                    <TableCell align='left' className={classes.tableHeadCell}>{translate.i18n('ACCOUNT')}</TableCell>
                                    <TableCell align='center' className={classes.tableHeadCell}>{translate.i18n('USERS')}</TableCell>
                                    <TableCell align='center' className={classes.tableHeadCell}>Type</TableCell>
                                    <TableCell align='center' className={classes.tableHeadCell}>Modules</TableCell>
                                    <TableCell align='center' className={classes.tableHeadCell}>Site</TableCell>
                                    <TableCell align='center' className={classes.tableHeadCell}>Actif</TableCell>
                                    <TableCell align='center' className={classes.tableHeadCell}></TableCell>
                                    <TableCell align='center' className={classes.tableHeadCell}></TableCell>
                                    <TableCell align='center' className={classes.tableHeadCell}></TableCell>
                                    <TableCell align='center' className={classes.tableHeadCell}></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {this.state.accounts.map((account, key) => <AccountRow
                                    key={key}
                                    index={key}
                                    account={account}
                                    editAccount={() => this.setState({ currentAccount: account, dialogManageAccountOpen: true })}
                                    cloneAccount={() => this.setState({ accountToDuplicate: account, dialogDuplicateAccountOpen: true })}
                                    exportAccount={() => this.setState({ currentAccount: account, dialogExportAccountOpen: true })}
                                    switchActiveAccount={() => this.setState({ dialogConfirmActive: { open: true, account } })}
                                />)}
                            </TableBody>
                        </Table>
                    }
                    {this.renderDialogExportAccount()}
                    {this.renderDialogManageAccount()}
                    {this.renderDialogDuplicateAccount()}
                    {this.renderDialogConfirm()}
                </Paper>
                <Pagination page={this.state.page} count={Math.ceil(this.state.count / 100)} style={{ marginLeft: 'auto', marginTop: '10px' }} onChange={(e, p) => this.setState({ page: p }, () => this.getAccounts())}/>
            </div>
        );
    }
}

export default withStyles(styles)(AccountsManagerPage);
